import { useState } from 'react';
import { requestPasswordReset } from '../../services/authService';

// Spinner component for loading state
const SpinnerIcon = () => (
  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

const ForgotPasswordForm = ({ onBack }) => {
  // States
  const [recoveryMethod, setRecoveryMethod] = useState('rut'); // 'rut' or 'email'
  const [rut, setRut] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formTouched, setFormTouched] = useState(false);
  const [validRut, setValidRut] = useState(false);
  const [validEmail, setValidEmail] = useState(false);

  // Environment variables
  const backendURL = import.meta.env.VITE_API_URL;

  // Validate and format RUT
  const formatRut = (value) => {
    // Remove everything except digits and 'K/k'
    let clean = value.replace(/[^0-9kK]/g, '').toUpperCase();
    
    // Limit to 9 characters max (8 digits + 1 verification digit)
    clean = clean.slice(0, 9);
    
    // If less than 2 characters, don't format yet
    if (clean.length < 2) return clean;
    
    // Split body and verification digit
    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    
    return `${body}-${dv}`;
  };

  // Validate RUT using Chilean algorithm
  const validateRut = (rut) => {
    if (!rut) return false;
    
    // Remove formatting
    const cleanRut = rut.replace(/[^0-9kK]/g, '').toUpperCase();
    if (cleanRut.length < 2) return false;
    
    const body = cleanRut.slice(0, -1);
    const dv = cleanRut.slice(-1);
    
    // Check minimum length for body
    if (body.length < 7) return false;
    
    // Calculate verification digit
    let sum = 0;
    let multiplier = 2;
    
    // Iterate from right to left
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body.charAt(i)) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    
    const expectedDV = 11 - (sum % 11);
    let calculatedDV;
    
    if (expectedDV === 11) calculatedDV = '0';
    else if (expectedDV === 10) calculatedDV = 'K';
    else calculatedDV = expectedDV.toString();
    
    return calculatedDV === dv;
  };

  // Validate email with regex
  const validateEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  };

  // Handle RUT input change
  const handleRutChange = (e) => {
    const input = e.target.value;
    const formatted = formatRut(input);
    setRut(formatted);
    setValidRut(validateRut(formatted));
    
    if (!formTouched) {
      setFormTouched(true);
    }
  };

  // Handle email input change
  const handleEmailChange = (e) => {
    const input = e.target.value;
    setEmail(input);
    setValidEmail(validateEmail(input));
    
    if (!formTouched) {
      setFormTouched(true);
    }
  };

  // Check if form is valid based on selected recovery method
  const isFormValid = recoveryMethod === 'rut' ? validRut : validEmail;

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Reset messages
    setError('');
    setSuccess('');
    
    // Form validation
    if (recoveryMethod === 'rut' && !validRut) {
      setError('Por favor, ingrese un RUT válido');
      return;
    }
    
    if (recoveryMethod === 'email' && !validEmail) {
      setError('Por favor, ingrese un correo electrónico válido');
      return;
    }
    
    setLoading(true);

    try {
      // Create request payload based on recovery method
      const payload = recoveryMethod === 'rut' 
        ? { rut: rut } 
        : { email: email };

      const response = await requestPasswordReset(payload);
      
      if (response) {
        setSuccess(response.message || 'Si el usuario existe, se enviaron instrucciones de recuperación al correo.');
        setRut('');
        setEmail('');
        setFormTouched(false);
      } else {
        setError(response.message || 'Error al procesar la solicitud');
      }
    } catch (error) {
      console.error('Password recovery error:', error);
      setError('Error de conexión. Por favor, intente más tarde.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="w-full max-w-md mx-auto" onSubmit={handleSubmit}>
      {/* Recovery Method Selector */}
      <div className="mb-6">
        <label 
          className="block text-customBlue font-bold mb-3"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          Recuperar contraseña por:
        </label>
        <div className="flex items-center justify-center mb-4">
          <div className="flex justify-between space-x-4 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              className={`py-2 px-6 rounded-md transition-all duration-200 focus:outline-none ${
                recoveryMethod === 'rut'
                  ? 'bg-white shadow-md text-customBlue font-bold'
                  : 'bg-transparent text-gray-500'
              }`}
              onClick={() => setRecoveryMethod('rut')}
            >
              RUT
            </button>
            <button
              type="button"
              className={`py-2 px-6 rounded-md transition-all duration-200 focus:outline-none ${
                recoveryMethod === 'email'
                  ? 'bg-white shadow-md text-customBlue font-bold'
                  : 'bg-transparent text-gray-500'
              }`}
              onClick={() => setRecoveryMethod('email')}
            >
              Correo
            </button>
          </div>
        </div>
      </div>

      {/* Conditional Input Field based on recovery method */}
      {recoveryMethod === 'rut' ? (
        <div className="mb-6">
          <label
            className="block text-customBlue font-bold mb-1"
            htmlFor="rut"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            RUT Socio <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="rut"
            placeholder="Ej: 12345678-9"
            value={rut}
            onChange={handleRutChange}
            className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 
              ${formTouched && !validRut && rut ? 
                'border-red-500 focus:ring-red-300' : 
                'border-customBlue focus:ring-customBlueLight'}
              transition-all duration-200`}
            required
          />
          {formTouched && !validRut && rut && (
            <p className="text-red-500 text-xs mt-1">RUT inválido. Revise el formato e intente nuevamente.</p>
          )}
        </div>
      ) : (
        <div className="mb-6">
          <label
            className="block text-customBlue font-bold mb-1"
            htmlFor="email"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Correo Electrónico <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            id="email"
            placeholder="nombre@ejemplo.com"
            value={email}
            onChange={handleEmailChange}
            className={`w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 
              ${formTouched && !validEmail && email ? 
                'border-red-500 focus:ring-red-300' : 
                'border-customBlue focus:ring-customBlueLight'}
              transition-all duration-200`}
            required
          />
          {formTouched && !validEmail && email && (
            <p className="text-red-500 text-xs mt-1">Por favor, ingrese un correo electrónico válido.</p>
          )}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        className={`w-full text-white py-2 rounded-md flex justify-center items-center transition-all duration-200 focus:outline-none
          ${isFormValid ? 
            'bg-customRed hover:bg-customRedLight active:bg-customRed' : 
            'bg-gray-400 cursor-not-allowed'}
        `}
        style={{ fontFamily: 'Nunito, sans-serif' }}
        disabled={loading || !isFormValid}
      >
        {loading ? <SpinnerIcon /> : 'Enviar instrucciones'}
      </button>

      {/* Back to Login Link */}
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={onBack}
          className="hover:underline text-customBlue focus:outline-none focus:ring-2 focus:ring-customBlueLight rounded px-2 py-1"
          style={{ fontFamily: 'Nunito, sans-serif' }}
        >
          Volver a inicio de sesión
        </button>
      </div>

      {/* Error and Success Messages */}
      {error && (
        <div className="mt-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded" role="alert">
          <p className="text-center text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
            {error}
          </p>
        </div>
      )}
      
      {success && (
        <div className="mt-4 p-2 bg-green-100 border border-green-400 text-green-700 rounded" role="alert">
          <p className="text-center text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
            {success}
          </p>
        </div>
      )}
    </form>
  );
};

export default ForgotPasswordForm;